"use client";

import { useEffect, useState, useCallback } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { isCompanyCrewUserClient } from "@/lib/crew/client";
import { ensurePushSubscription, syncPushSubscription } from "@/lib/push/subscribe-client";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_STORAGE_KEY = "pwa-install-dismissed-until";
const DISMISS_MS = 24 * 60 * 60 * 1000;
function readDismissedUntil(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return 0;
    const until = Number(raw);
    return Number.isFinite(until) ? until : 0;
  } catch {
    return 0;
  }
}

function isDismissSnoozed(): boolean {
  return readDismissedUntil() > Date.now();
}

function snoozeDismiss(ms: number = DISMISS_MS) {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now() + ms));
  } catch {
    /* private mode / quota — still hide for this session via state */
  }
}

function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia("(display-mode: standalone)");
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mq.matches || iosStandalone;
}

/**
 * Install / push prompts — centred card (never a full-screen tap blocker).
 * Field app still shows the install prompt when not on the home screen;
 * push opt-in stays in crew notification settings.
 */
export function PwaClient({ installBrandName = null }: { installBrandName?: string | null }) {
  const pathname = usePathname() ?? "";
  const { data: session, status } = useSession();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [pushState, setPushState] = useState<"idle" | "loading" | "enabled" | "unsupported">("idle");
  const [dismissed, setDismissed] = useState(false);
  const [dismissReady, setDismissReady] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [crewFieldName, setCrewFieldName] = useState<string | null>(null);

  const vapidPublic = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? "";
  const isCrew = isCompanyCrewUserClient(
    session?.user as { companyId?: string; role?: string; scafferAccountKind?: string | null } | undefined
  );
  const onCrewApp = pathname.startsWith("/app/crew");
  const wantsBrandLabel = isCrew || onCrewApp || Boolean(installBrandName);

  useEffect(() => {
    setDismissed(isDismissSnoozed());
    setDismissReady(true);
  }, []);

  useEffect(() => {
    if (!wantsBrandLabel || status !== "authenticated") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/workspace/brand");
        if (!res.ok) return;
        const data = (await res.json()) as { fieldAppName?: string };
        if (!cancelled && data.fieldAppName) setCrewFieldName(data.fieldAppName);
      } catch {
        /* ignore — falls back to platform default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wantsBrandLabel, status]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncInstalled = () => setInstalled(isStandaloneDisplay());
    syncInstalled();
    const mq = window.matchMedia("(display-mode: standalone)");
    mq.addEventListener("change", syncInstalled);
    return () => mq.removeEventListener("change", syncInstalled);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = navigator.userAgent || "";
    const iOS = /iphone|ipad|ipod/i.test(ua);
    const iPadOS =
      navigator.platform === "MacIntel" &&
      ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1;
    setIsIos(iOS || iPadOS);
  }, []);

  const dismissPrompt = useCallback(() => {
    snoozeDismiss();
    setDismissed(true);
  }, []);

  const runInstall = useCallback(async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
    setInstalled(true);
  }, [installEvent]);

  const enablePush = useCallback(async () => {
    if (!vapidPublic || !session?.user) return;
    setPushState("loading");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setPushState("idle");
        return;
      }
      const sub = await ensurePushSubscription(vapidPublic);
      if (!sub) {
        setPushState("unsupported");
        return;
      }
      const ok = await syncPushSubscription(sub, true);
      setPushState(ok ? "enabled" : "idle");
      if (ok) {
        snoozeDismiss();
        setDismissed(true);
      }
    } catch {
      setPushState("unsupported");
    }
  }, [vapidPublic, session?.user]);

  useEffect(() => {
    if (typeof window === "undefined" || !vapidPublic || !session?.user) return;
    if (!("Notification" in window) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (Notification.permission !== "granted") return;
        const sub = await ensurePushSubscription(vapidPublic);
        if (!sub) return;
        await syncPushSubscription(sub, false);
        if (!cancelled) setPushState("enabled");
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vapidPublic, session?.user]);

  if (installed) return null;
  if (!dismissReady || dismissed) return null;

  const showInstall = Boolean(installEvent);
  const showIosInstall = status === "authenticated" && isIos && !Boolean(installEvent);
  // Push opt-in on crew lives in Settings — keep this prompt install-focused there.
  const showPush =
    !onCrewApp &&
    status === "authenticated" &&
    Boolean(vapidPublic) &&
    pushState !== "unsupported" &&
    pushState !== "enabled" &&
    typeof Notification !== "undefined";

  if (!showInstall && !showIosInstall && !showPush) return null;

  const brandShort = installBrandName?.trim() || null;
  const fieldLabel =
    crewFieldName ?? (brandShort ? `${brandShort} Field` : "Scaff Field");
  const headline =
    onCrewApp || isCrew
      ? `Add ${fieldLabel} to your home screen for the full-screen app (no browser bars).`
      : brandShort
        ? `Add ${brandShort} to your home screen for quick access to jobs and quotes.`
        : "Scaffolder Near Me — install for quick access to ScaffOps™ & quotes.";

  const homeScreenLabel = brandShort ?? fieldLabel;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center p-4"
      aria-hidden={false}
    >
      <div
        className="pointer-events-auto w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_12px_40px_rgba(0,0,0,0.18)]"
        role="region"
        aria-label="App install and notifications"
      >
        <div className="flex flex-col gap-3">
          <p className="text-center text-sm font-medium leading-snug text-zinc-800">{headline}</p>
          {showIosInstall && (
            <p className="rounded-lg bg-zinc-50 px-2.5 py-2 text-center text-xs leading-snug text-zinc-600">
              Tap the Share icon in Safari, then{" "}
              <span className="font-semibold">Add to Home Screen</span>. The home screen icon will
              show as <span className="font-semibold">{homeScreenLabel}</span>.
            </p>
          )}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {showInstall && (
              <button
                type="button"
                onClick={runInstall}
                className="rounded-lg bg-brand-yellow px-3 py-2 text-sm font-semibold text-brand-dark hover:bg-brand-yellow/90"
              >
                Install app
              </button>
            )}
            {showPush && pushState !== "loading" && (
              <button
                type="button"
                onClick={enablePush}
                className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-800 hover:bg-zinc-50"
              >
                Quote alerts
              </button>
            )}
            {pushState === "loading" && (
              <span className="text-xs text-zinc-500">Enabling…</span>
            )}
            <button
              type="button"
              onClick={dismissPrompt}
              className="rounded-lg px-2 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
              aria-label="Dismiss"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

